// src/api/backgrounds.ts
import { BACKEND } from '../../config';
import { csrfFetch } from '../../utils/csrfFetch';

export type UiPreferences = {
  textColor?: string;
  sidebarTextColor?: string;
  textOpacity?: string;
  surfaceOpacity?: string;
  surfaceColor?: string;
  sidebarSurfaceColor?: string;
  surfaceBlur?: string;
};

export type BgPayload = {
  mainUrl: string | null;
  sideUrl: string | null;
  mainLocalPath?: string | null;
  sideLocalPath?: string | null;
  uiPreferences?: UiPreferences;
};

/**
 * Получаем CSRF-токен напрямую из ответа /api/csrf/
 * (не пытаемся читать document.cookie — он другой origin).
 */
async function getCsrfToken(): Promise<string> {
  const res = await fetch(`${BACKEND}/api/csrf/`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to fetch CSRF endpoint');

  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  const token = data?.csrftoken || data?.csrfToken;
  if (!token || typeof token !== 'string' || token.length < 32) {
    throw new Error('CSRF token not returned by backend');
  }
  return token;
}

export async function getMyBackgrounds(): Promise<BgPayload> {
  const res = await fetch(`${BACKEND}/api/backgrounds/me/`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || 'Failed to load backgrounds');
  }
  return res.json();
}

export async function uploadBackground(target: 'main' | 'side', file: File): Promise<BgPayload> {
  await getCsrfToken();
  const fd = new FormData();
  fd.append('file', file);

  const res = await csrfFetch(`${BACKEND}/api/backgrounds/upload/?target=${target}`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    // пробуем вытащить понятное сообщение
    try {
      const j = await res.json();
      throw new Error(j?.error || 'Upload failed');
    } catch {
      const msg = await res.text().catch(() => '');
      throw new Error(msg || 'Upload failed');
    }
  }
  return res.json();
}

export async function deleteBackground(target: 'main' | 'side'): Promise<BgPayload> {
  await getCsrfToken();

  const res = await csrfFetch(`${BACKEND}/api/backgrounds/delete/?target=${target}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    try {
      const j = await res.json();
      throw new Error(j?.error || 'Delete failed');
    } catch {
      const msg = await res.text().catch(() => '');
      throw new Error(msg || 'Delete failed');
    }
  }
  return res.json();
}

export async function saveLocalBackgroundPath(
  target: 'main' | 'side',
  localPath: string
): Promise<BgPayload> {
  await getCsrfToken();
  const res = await csrfFetch(`${BACKEND}/api/backgrounds/local-path/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ target, localPath }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || 'Failed to save local background path');
  }
  return res.json();
}

export async function saveUiPreferences(uiPreferences: UiPreferences): Promise<BgPayload> {
  await getCsrfToken();
  const res = await csrfFetch(`${BACKEND}/api/backgrounds/preferences/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ uiPreferences }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(msg || 'Failed to save UI preferences');
  }
  return res.json();
}
